import { VenueStorefrontBookingTracking } from './venue-storefront-booking-tracking.service'
import { choiceTrackingContext } from './venue-storefront-booking-mappers'
import { CurrencyPipe, DatePipe, NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input } from '@angular/core'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { take } from 'rxjs'
import { CurrencyUnitPipe } from '../../../pipes/currency-unit.pipe'
import { ReasonTimePipe } from '../../../pipes/reason-time.pipe'
import { richTextPlainText } from '../../../utilities/rich-text'
import { VenueColourDirective } from '../../../directives/venue-colour.directive'
import { Venue } from '../../../domain/Venue'
import { bookingPaymentTerminologyForVenueId } from '../../../domain/BookingPaymentTerminology'
import { ConfirmationComponent } from '../../../confirmation/confirmation.component'
import { WaitlistFormComponent } from '../../../waitlist-form/waitlist-form.component'
import { VenueStorefrontBookingDetailsStepComponent } from '../venue-storefront-booking-details-step/venue-storefront-booking-details-step.component'
import { VenueStorefrontBookingPolicyStepComponent } from '../venue-storefront-booking-policy-step/venue-storefront-booking-policy-step.component'
import { VenueStorefrontBookingSlotStepComponent } from '../venue-storefront-booking-slot-step/venue-storefront-booking-slot-step.component'
import { VenueStorefrontBookingUpsellStepComponent } from '../venue-storefront-booking-upsell-step/venue-storefront-booking-upsell-step.component'
import { VenueStorefrontEventModalComponent } from '../venue-storefront-event-modal/venue-storefront-event-modal.component'
import { VenueStorefrontBookingSearchComponent } from '../venue-storefront-booking-search/venue-storefront-booking-search.component'
import { VenueStorefrontBookingProgressSummaryComponent } from '../venue-storefront-booking-progress-summary/venue-storefront-booking-progress-summary.component'
import { VenueStorefrontBookingReasonStepComponent } from '../venue-storefront-booking-reason-step/venue-storefront-booking-reason-step.component'
import { VenueStorefrontBookingSummaryCardComponent } from '../venue-storefront-booking-summary-card/venue-storefront-booking-summary-card.component'
import { BookingProductRefundPolicyComponent } from '../venue-storefront-booking-summary-card/booking-product-refund-policy.component'
import { VenueStorefrontBookingPaymentElementComponent } from '../venue-storefront-booking-payment-element/venue-storefront-booking-payment-element.component'
import { VenueStorefrontBookingFlow } from './venue-storefront-booking-flow'
import type {
    VenueStorefrontBookingChoice,
    VenueStorefrontBookingSearch,
} from './venue-storefront-booking-flow.types'

@Component({
    selector: 'app-venue-storefront-booking-flow',
    standalone: true,
    imports: [
        BookingProductRefundPolicyComponent,
        ConfirmationComponent,
        VenueStorefrontBookingPaymentElementComponent,
        CurrencyUnitPipe,
        DatePipe,
        NgTemplateOutlet,
        VenueColourDirective,
        VenueStorefrontBookingDetailsStepComponent,
        VenueStorefrontBookingPolicyStepComponent,
        VenueStorefrontBookingProgressSummaryComponent,
        VenueStorefrontBookingReasonStepComponent,
        VenueStorefrontBookingSearchComponent,
        VenueStorefrontBookingSlotStepComponent,
        VenueStorefrontBookingSummaryCardComponent,
        VenueStorefrontBookingUpsellStepComponent,
        WaitlistFormComponent,
    ],
    providers: [CurrencyPipe],
    templateUrl: './venue-storefront-booking-flow.component.html',
    styleUrls: ['./venue-storefront-booking-flow.component.sass'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VenueStorefrontBookingFlowComponent {
    @Input({ required: true }) venue!: Venue

    constructor(
        protected readonly tracking: VenueStorefrontBookingTracking,
        protected readonly flow: VenueStorefrontBookingFlow,
        private modalService: NgbModal
    ) {}

    protected get paymentTerminology() {
        return bookingPaymentTerminologyForVenueId(this.venue.id)
    }

    protected showChoiceDetails(
        choice: VenueStorefrontBookingChoice,
        search: VenueStorefrontBookingSearch
    ) {
        if (choice.kind === 'standard') {
            this.flow.selectChoice(choice)
            return
        }
        if (choice.kind === 'reason' && !richTextPlainText(choice.reason.description)) {
            const reason = choice.reason
            const openingTimes = reason.displayedOpeningTimes
                ?? (reason.scheduleRules.length > 0
                    ? new ReasonTimePipe().transform(reason, search.idealTime)
                    : null)
            if (!openingTimes?.trim()) {
                this.flow.selectChoice(choice)
                return
            }
        }
        const context = choiceTrackingContext(choice)
        this.tracking.record('choice_viewed', context)
        const modal = this.modalService.open(VenueStorefrontEventModalComponent, {
            ariaLabelledBy: 'venue-storefront-event-modal-title',
            centered: true,
            fullscreen: 'sm',
            scrollable: true,
            size: 'lg',
        })
        modal.dismissed.pipe(take(1)).subscribe(() => this.tracking.record('choice_dismissed', context))
        const component = modal.componentInstance as VenueStorefrontEventModalComponent
        if (choice.kind === 'event') {
            const occurrenceDateTime = choice.event.recurrence.startTime
                ?.dateTimeOnDate(search.date) ?? new Date(search.date)
            component.configure(choice.event, occurrenceDateTime, this.venue.currencyCode)
        } else {
            component.configureReason(choice.reason, search.idealTime)
        }
        if (choice.kind === 'event' && choice.event.notReservableOnline) {
            return
        }
        component.enableBookingSelection()
        modal.closed
            .pipe(take(1))
            .subscribe(() => this.flow.selectChoice(choice))
    }
}
